import {Circle, LayersControl, MapContainer, Marker, Polyline, Popup, TileLayer, Tooltip, useMap, useMapEvent} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import React, {useEffect} from "react";
import L from "leaflet";
import LocalStorage from "../../../../../../util/localStorage";

const DEST_ICON_W = 90
const DEST_ICON_H = 30

export default function MapCanvas({
                                      center,
                                      zoom = 13,
                                      onClick,
                                      areMarkerClustered,
                                      searchRadius,
                                      data,
                                      fitBoundsRef,
                                      onGetCustomIcon,
                                      onGetPopupContent,
                                      routeSegments,
                                      routes
                                  }) {
    let markers = Array.isArray(data) ? data : [data];
    const userSelectedLayer = LocalStorage.get("user_map_settings")?.selectedLayer ?? "Default";

    markers = markers.filter(marker => !!marker?.Latitude && !!marker?.Longitude);
    const fallbackCenter = [markers?.[0]?.Latitude, markers?.[0]?.Longitude];
    const mapCenter = center ?? fallbackCenter;

    const layers = [
        {
            name: "Default",
            url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
            attribution: "&copy; <a href=&quot;http://osm.org/copyright&quot;>OpenStreetMap</a> contributors"
        },
        {
            name: "Dark mode",
            url: "https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png",
            attribution: "&copy; <a href=\"http://www.openstreetmap.org/copyright\">OpenStreetMap</a> &copy; <a href=\"http://cartodb.com/attributions\">CartoDB</a>"
        },
        {
            name: "Satellite",
            url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
            attribution: "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
        },
        {
            name: "Google-Map",
            url: "https://mt1.google.com/vt/lyrs=r&x={x}&y={y}&z={z}",
            attribution: "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
        },
        {
            name: "Google-Hybrid",
            url: "https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}",
            attribution: "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
        }
    ];

    return (
        <MapContainer
            center={mapCenter}
            zoom={zoom}
            style={{height: '100%', width: '100%'}}
        >
            <ChangeMapView center={mapCenter} zoom={zoom}/>

            {!!fitBoundsRef && (
                <FitMapBounds markers={markers} fitBoundsRef={fitBoundsRef}/>
            )}

            <LayersControl position="topright">
                {layers.map(layer => (
                    <LayersControl.BaseLayer
                        key={layer.name}
                        checked={layer.name === userSelectedLayer}
                        name={layer.name}
                    >
                        <TileLayer
                            url={layer.url}
                            attribution={layer.attribution}
                        />
                    </LayersControl.BaseLayer>
                ))}
            </LayersControl>

            {routes && routes.map((route, i) => {
                const destIcon = L.divIcon({
                    className: '',
                    html: `<div style="display:flex;flex-direction:column;align-items:center;gap:1px;font-family:sans-serif;width:${DEST_ICON_W}px">
                        <div style="width:100%;background:white;border:1px solid ${route.color};border-radius:3px;padding:1px 5px;box-shadow:0 1px 2px rgba(0,0,0,0.1);box-sizing:border-box;line-height:1.3">
                            <div style="font-size:9px;font-weight:700;color:#111827;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${route.truckNumber}${route.whenLabel ? `<span style="font-weight:400;color:${route.color};margin-left:3px">${route.whenLabel}</span>` : ''}</div>
                            <div style="font-size:8px;color:#6b7280;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${route.whereShort ?? route.whereLabel ?? ''}</div>
                        </div>
                        <div style="width:4px;height:4px;border-radius:50%;background:${route.color};border:1px solid white;box-shadow:0 1px 2px rgba(0,0,0,0.2)"></div>
                    </div>`,
                    iconSize: [DEST_ICON_W, DEST_ICON_H],
                    iconAnchor: [DEST_ICON_W / 2, DEST_ICON_H],
                })
                return (
                    <Marker key={i} position={[route.toLat, route.toLng]} icon={destIcon}>
                        <Popup>{[route.whenLabel, route.whereLabel].filter(Boolean).join(' · ')}</Popup>
                    </Marker>
                )
            })}

            {!areMarkerClustered && !!markers.length && markers.map((marker, index) => (
                <Marker
                    key={index}
                    position={[marker.Latitude, marker.Longitude]}
                    {...(onGetCustomIcon ? { icon: onGetCustomIcon(marker) } : {})}
                >
                    <Popup>
                        {onGetPopupContent ? onGetPopupContent(marker) : <MarkerPopup marker={marker}/>}
                    </Popup>
                </Marker>
            ))}

            {areMarkerClustered && !!markers.length && (
                <MarkerClusterGroup>
                    {markers.map((marker, index) => (
                        <Marker
                            key={index}
                            position={[marker.Latitude, marker.Longitude]}
                            {...(onGetCustomIcon ? { icon: onGetCustomIcon(marker) } : {})}
                        >
                            <Popup>
                                {onGetPopupContent ? onGetPopupContent(marker) : <MarkerPopup marker={marker}/>}
                            </Popup>
                        </Marker>
                    ))}
                </MarkerClusterGroup>
            )}

            {routeSegments && routeSegments.filter(seg => seg.from && seg.to).map((seg, i) => {
                const color = seg.type === 'onLoad' ? '#3b82f6'
                    : (seg.type === 'empty' && seg.isStale) ? '#ef4444'
                    : seg.type === 'empty' ? '#22c55e'
                    : '#9ca3af'
                const pathOptions = seg.type === 'onLoad'
                    ? {color, weight: 3}
                    : seg.type === 'manual'
                    ? {color, weight: 1.5, dashArray: '4 4'}
                    : {color, weight: 2, dashArray: '8 5'}
                const escapedLabel = seg.label
                    ? seg.label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                    : null
                const labelIcon = (escapedLabel && seg.midpoint) ? L.divIcon({
                    className: '',
                    html: `<div style="background:white;color:${color};font-size:10px;font-weight:600;padding:2px 6px;border-radius:10px;border:1px solid ${color};white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,0.15);transform:translateX(-50%);width:max-content">${escapedLabel}</div>`,
                    iconSize: [1, 1],
                    iconAnchor: [0, 10],
                }) : null
                return (
                    <React.Fragment key={`seg-${i}`}>
                        <Polyline positions={[seg.from, seg.to]} pathOptions={pathOptions}>
                            {seg.isStale && (
                                <Tooltip>
                                    Empty mileage may be inaccurate — locations were added or changed after dispatch.
                                </Tooltip>
                            )}
                        </Polyline>
                        {labelIcon && (
                            <Marker position={seg.midpoint} icon={labelIcon} interactive={false}/>
                        )}
                    </React.Fragment>
                )
            })}

            {onClick && <MapClickHandler onClick={onClick}/>}

            {searchRadius?.center?.[0] && searchRadius?.center?.[1] && (
                <Circle
                    center={searchRadius.center}
                    radius={searchRadius.radius}
                />
            )}

            <LayerChangeHandler layers={layers}/>
        </MapContainer>
    );
}


function ChangeMapView({center, zoom}) {
    const map = useMap();

    useEffect(() => {
        if (center && zoom !== undefined) {
            map.setView(center, zoom);
        }
    }, [center, zoom, map]);

    return null;
}

function FitMapBounds({markers, fitBoundsRef}) {
    const map = useMap();

    if (fitBoundsRef) {
        fitBoundsRef.current = () => {
            const bounds = L.latLngBounds(
                markers.map(marker => [marker.Latitude, marker.Longitude])
            );
            if (bounds.isValid()) {
                map.fitBounds(bounds, {padding: [50, 50]});
            }
        };
    }

    return null;
}

function MapClickHandler({onClick}) {
    useMapEvent('click', (e) => {
        onClick(e);
    });
    return null;
}

export function MarkerPopup({marker}) {
    const s = marker._status
    const headingTo  = s?.headingTo  ?? null
    const currentLoc = s?.locationLabel ?? [marker.CityName, marker.State].filter(Boolean).join(', ') ?? null

    const cat = s?.category
    const availDate = s?.availableDate
    let whenText = null
    if (cat === 'now')       whenText = 'Available now'
    else if (cat === 'today')    whenText = 'Available today'
    else if (cat === 'tomorrow') whenText = 'Available tomorrow'
    else if (cat === 'day_after') whenText = 'Available in 2 days'
    else if (availDate)          whenText = 'Available ' + availDate.toLocaleDateString('en-US', {month: 'short', day: 'numeric'})

    return (
        <div style={{minWidth: '160px', fontFamily: 'sans-serif'}}>
            <div style={{fontWeight: '700', fontSize: '14px', marginBottom: '6px', borderBottom: '1px solid #e5e7eb', paddingBottom: '4px'}}>
                Truck {marker.Truck}
            </div>
            {marker.Driver && (
                <div style={{fontSize: '12px', color: '#6b7280', marginBottom: '6px'}}>{marker.Driver}</div>
            )}
            {currentLoc && (
                <div style={{fontSize: '12px', marginBottom: '4px'}}>
                    <span style={{color: '#9ca3af', fontSize: '11px', display: 'block'}}>Current location</span>
                    {currentLoc}
                </div>
            )}
            {headingTo && (
                <div style={{fontSize: '12px', marginBottom: '4px'}}>
                    <span style={{color: '#9ca3af', fontSize: '11px', display: 'block'}}>Headed to</span>
                    <strong>{headingTo}</strong>
                </div>
            )}
            {whenText && (
                <div style={{fontSize: '12px', fontWeight: '600', marginTop: '6px', color: headingTo ? '#0e7490' : '#16a34a'}}>
                    {whenText}
                </div>
            )}
        </div>
    )
}

function LayerChangeHandler({layers}) {
    useMapEvent('layeradd', (event) => {
        const layer = layers.find(l => l.url === event.layer._url);
        if (layer) {
            LocalStorage.set("user_map_settings", {selectedLayer: layer.name});
        }
    });
    return null;
}